import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    OVERVIEW_ERROR_KEYS,
    OverviewCardSkeleton,
    OverviewEmptyCard,
    resolveEmptyBodyKey,
} from '../overviewCard';
import { CASH_RUNWAY as META } from './cashRunwayMeta';
import { CashRunwayView } from './CashRunwayView';
import { useCashRunway, type CashRunwayData } from './useCashRunway';

/**
 * Self-served cash runway card: owns its slice of the Financial Position read and
 * its own `ErrorBoundary`, so a failure here cannot take the page or its
 * siblings with it.
 */
export const CashRunway: React.FC = () => {
    const page = useCashRunway();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t(OVERVIEW_ERROR_KEYS.title),
            errorMessage: t(OVERVIEW_ERROR_KEYS.message),
            errorRetry: t(OVERVIEW_ERROR_KEYS.retry),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid={`${META.slot}-error-boundary`}
            fallbackMessage={copy.errorMessage}
            id={`${META.slot}-error-boundary`}
            onRetry={page.onRetry}
            resetKeys={[page.error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <CashRunwayContent
                data={page.data}
                error={page.error}
                isLoading={page.isLoading}
                missing={page.missing}
                onEmptyAction={page.onEmptyAction}
            />
        </ErrorBoundary>
    );
};

type CashRunwayContentProps = {
    data: CashRunwayData | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    missing: readonly string[];
    onEmptyAction: () => void;
};

/**
 * Sits *inside* the boundary so the throw below is caught by it — React does not
 * catch a component's own throws.
 */
const CashRunwayContent: React.FC<CashRunwayContentProps> = ({
    data,
    error,
    isLoading,
    missing,
    onEmptyAction,
}) => {
    const { t } = useTranslation('pages');

    if (error) {
        throw error;
    }

    if (isLoading) {
        return (
            <OverviewCardSkeleton
                icon={META.icon}
                question={t(META.keys.question)}
                slot={META.slot}
                title={t(META.keys.title)}
            />
        );
    }

    if (!data) {
        return (
            <OverviewEmptyCard
                body={t(resolveEmptyBodyKey(META.keys.emptyBody, missing))}
                cta={{
                    label: t(META.keys.emptyCta),
                    icon: META.ctaIcon,
                    onPress: onEmptyAction,
                }}
                icon={META.icon}
                question={t(META.keys.question)}
                slot={META.slot}
                title={t(META.keys.title)}
            />
        );
    }

    return <CashRunwayView data={data} />;
};
